import LegalShell from "@/components/LegalShell";
import { Mail, MessageSquare, BookOpen, Clock } from "lucide-react";
import { billingUIEnabled, isIOSNative } from "@/lib/platform";

export default function Support() {
    // Billing FAQs are web-only — native shells must not reference
    // subscriptions/plans (App Store Guideline 3.1.1).
    const showBilling = billingUIEnabled();
    // Apple Guideline 2.3.10 (Accurate Metadata): iOS Store review flagged
    // us on Feb 10, 2026 for referencing Android in release-notes metadata.
    // Belt-and-suspenders: also hide Android references from any in-app
    // page a reviewer might browse on iOS. Android + web builds still see
    // the cross-platform blurb.
    const hideAndroidRefs = isIOSNative();
    return (
        <LegalShell
            title="Support"
            subtitle="We answer every email personally. No bots, no ticket queues."
        >
            <div data-testid="support-channels" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14,
                margin: "8px 0 30px",
            }}>
                <ContactCard
                    icon={<Mail size={20} color="var(--bm-gold)" />}
                    title="Email"
                    body="captain@bosunsmate.app"
                    href="mailto:captain@bosunsmate.app"
                    testid="support-email-card"
                />
                <ContactCard
                    icon={<Clock size={20} color="var(--bm-gold)" />}
                    title="Response time"
                    body="Within 24 hours · 7 days a week"
                    testid="support-response-card"
                />
                <ContactCard
                    icon={<BookOpen size={20} color="var(--bm-gold)" />}
                    title="FAQ"
                    body="Common questions, below ↓"
                    testid="support-faq-card"
                />
            </div>

            <h2>Frequently asked questions</h2>

            {showBilling && (
                <>
                    <h3>How do I cancel my subscription?</h3>
                    <p>
                        Sign in → click the gear icon on your dashboard → "Manage
                        subscription" → cancel via Stripe's billing portal. Your data stays
                        intact in read-only mode after cancellation.
                    </p>

                    <h3>What happens to my data if I cancel?</h3>
                    <p>
                        Nothing — it's preserved exactly as you left it. If you re-subscribe
                        later, everything is right where you left it. If you'd like your
                        data fully deleted, email us and we'll erase it within 14 days.
                    </p>
                </>
            )}

            <h3>How do I export my vessel records?</h3>
            <p>
                Open the vessel on your dashboard → click the "Export PDF" button
                in the header. You'll get a complete, printable record of every
                entry — perfect for selling a boat, insurance claims, or year-end
                bookkeeping.
            </p>

            <h3>Can I share my vessel with my partner / co-owner / mechanic?</h3>
            <p>
                Multi-user vessel sharing is on our roadmap. For now, the
                simplest workaround is sharing the account login with a trusted
                co-owner. We don't recommend sharing with service providers — wait
                for the proper sharing feature.
            </p>

            <h3>Is my photo / receipt data backed up?</h3>
            <p>
                Yes — daily backups, encrypted at rest, transmitted over HTTPS only.
                We retain backups for 30 days.
            </p>

            <h3>I forgot my password — how do I reset it?</h3>
            <p>
                On the sign-in page, click "Forgot password" and enter your email.
                You'll get a reset link within a minute. The link is valid for 30
                minutes and single-use.
            </p>

            {showBilling && (
                <>
                    <h3>How do I switch from Monthly to Annual (or vice versa)?</h3>
                    <p>
                        Go to the Pricing page and click the plan you want. We'll
                        automatically cancel your previous subscription and start the new
                        one. No double-billing.
                    </p>
                </>
            )}

            <h3 data-testid="support-mobile-heading">{hideAndroidRefs ? "Do you have an iOS app?" : "Do you have an iOS or Android app?"}</h3>
            <p data-testid="support-mobile-body">
                {hideAndroidRefs ? (
                    <>
                        Yes — you&apos;re using it right now. You can also use Bosun&apos;s Mate
                        directly in any modern browser at <a className="bm-link" href="https://bosunsmate.app">bosunsmate.app</a>, or install it as a PWA on your phone home screen.
                    </>
                ) : (
                    <>
                        Yes — both are available. You can also use the app directly in any
                        modern browser at <a className="bm-link" href="https://bosunsmate.app">bosunsmate.app</a>, or install it as a PWA on your phone home screen.
                    </>
                )}
            </p>

            <h3>Something else?</h3>
            <p>
                Email <a className="bm-link" href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</a> — we read everything and reply within 24 hours.
            </p>
        </LegalShell>
    );
}

function ContactCard({ icon, title, body, href, testid }) {
    const card = (
        <div data-testid={testid} style={{
            padding: 18,
            background: "var(--bm-panel, rgba(12, 24, 40, 0.4))",
            border: "1px solid var(--bm-border-soft)",
            borderRadius: 12,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            minHeight: 88,
        }}>
            {icon}
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-gold)", marginTop: 4 }}>
                {title}
            </div>
            <div style={{ color: "var(--bm-text)", fontSize: 14 }}>{body}</div>
        </div>
    );
    if (href) {
        return <a href={href} style={{ textDecoration: "none" }}>{card}</a>;
    }
    return card;
}
